// src/pages/Dashboard/vendor-balance/components/VendorBalanceChart.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from './VendorBalanceChart.module.css';
import { vendorBalanceApi } from '../../../../api/vendor-balance/vendorBalanceApi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const VendorBalanceChart = () => {
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await vendorBalanceApi.getVendorBalanceStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to load vendor balance statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className={styles.wrapper}><div className={styles.loadingMessage}>Loading vendor balance statistics...</div></div>;
  if (error) return <div className={styles.wrapper}><div className={styles.errorMessage}>{error}</div></div>;
  if (!stats) return null;

  const chartData = {
    labels: ['0-30 Days', '31-60 Days', '61-90 Days', '91+ Days'],
    datasets: [{
      label: 'Amount (₹)',
      data: [
        stats.breakdown.amount0to30,
        stats.breakdown.amount31to60,
        stats.breakdown.amount61to90,
        stats.breakdown.amount91Plus
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(234, 179, 8, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgb(59, 130, 246)',
        'rgb(251, 146, 60)',
        'rgb(234, 179, 8)',
        'rgb(239, 68, 68)'
      ],
      borderWidth: 2,
      borderRadius: 8,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        callbacks: { label: (ctx) => '₹' + ctx.parsed.y.toLocaleString('en-IN') }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => '₹' + (v / 1000000).toFixed(1) + 'M' },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      x: { grid: { display: false } }
    }
  };

  const formatCurrency = (amount) => '₹' + (amount || 0).toLocaleString('en-IN');

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>Vendor Balance Overview</h2>
      </div>

      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardPrimary}`}>
          <div className={styles.statLabel}>Total Overdue</div>
          <div className={styles.statValue}>{formatCurrency(stats.totalOverdue)}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Overdue Vendors</div>
          <div className={`${styles.statValue} ${styles.statValueDanger}`}>{stats.totalVendors}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Vendors 0-30 Days</div>
          <div className={styles.statValue}>{stats.breakdown.vendors0to30}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount0to30)}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Vendors 31-60 Days</div>
          <div className={styles.statValue}>{stats.breakdown.vendors31to60}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount31to60)}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Vendors 61-90 Days</div>
          <div className={styles.statValue}>{stats.breakdown.vendors61to90}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount61to90)}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Vendors 91+ Days</div>
          <div className={styles.statValue}>{stats.breakdown.vendors91Plus}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount91Plus)}</div>
        </div>
      </div>

      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <h3 className={styles.sectionTitle}>Aging Analysis by Amount</h3>
        </div>
        <div className={styles.chartContainer}>
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default VendorBalanceChart;